import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { UntypedFormArray, UntypedFormControl } from '@angular/forms'
import { LanguageService } from '@spartacus/core'
import { ICON_TYPE } from '@spartacus/storefront'
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop'
import { of } from 'rxjs'
import { map, take } from 'rxjs/operators'
import {
  EbFieldConfig,
  EbFieldOptionConfig,
  EbLocalizedString
} from '../../interfaces/eb-form.interface'
import { EbFormService } from '../../services/eb-form.service'

@Component({
    selector: 'eb-form-field',
    templateUrl: './eb-form-field.component.html',
    styleUrls: ['./eb-form-field.component.scss'],
    standalone: false
})
export class EbFormFieldComponent {
  @Input() fieldConfig: EbFieldConfig
  @Input() control: UntypedFormControl | UntypedFormArray
  @Input() path: string
  @Input() context: string
  @Input() disabled: boolean
  @Input() readonly: boolean
  @Output() changed = new EventEmitter<{ path: string; value: string }>()
  @Output() changing = new EventEmitter<{ path: string; value: string }>()
  @Output() next = new EventEmitter<void>()
  toCamelCaseString: (str: string) => string
  toCamelCasePath: (str: string) => string
  toDashedString: (str: string) => string
  readonly iconTypes = ICON_TYPE
  showRecaptcha = false
  dirtyTimeout = null
  droppedFiles: NgxFileDropEntry[] = []
  @ViewChild('fileInput') fileInput: HTMLInputElement

  constructor(
    protected formService: EbFormService,
    protected languageService: LanguageService,
    protected cd: ChangeDetectorRef
  ) {
    this.toCamelCaseString = formService.toCamelCaseString
    this.toCamelCasePath = formService.toCamelCasePath
    this.toDashedString = formService.toDashedString
  }

  get selectOptions() {
    const localizedPlaceholder =
      this.fieldConfig.placeholder && this.fieldConfig.placeholder.default !== ''
        ? this.resolveLocalizedString(this.fieldConfig.placeholder)
        : this.createI18nToken(this.fieldConfig.placeholderToken || 'placeholder')
    const placeholderOption = {
      name: '',
      labelToken: localizedPlaceholder
    } as EbFieldOptionConfig
    return this.fieldConfig.options.concat(placeholderOption)
  }

  get i18nPrefix(): string {
    const contextPrefix = this.context ? this.toDottedString(this.context) + '.' : ''
    const pathPrefix = this.toCamelCasePath(this.path) + '.'
    return `${contextPrefix}${pathPrefix}`
  }

  get error(): boolean {
    return (
      (!this.fieldConfig.readonly &&
        !this.disabled &&
        this.control.invalid &&
        this.control.touched) ||
      undefined
    )
  }

  get formArray() {
    return this.fieldConfig.multiple ? (this.control as UntypedFormArray) : null
  }

  // EB-17839 refresh input to receive autofill values
  onRefreshInputFields() {
    this.cd.detectChanges()
  }

  onChanging(event: KeyboardEvent, catchEnter) {
    const value = this.getInputValueFromEvent(event)
    if (catchEnter && event.key === 'Enter') {
      if (this.dirtyTimeout) {
        clearTimeout(this.dirtyTimeout)
      }
      this.changed.emit({ path: this.path, value: this.control.value })
      this.next.emit()
      event.preventDefault()
      event.stopPropagation()
    } else {
      if (!this.dirtyTimeout) {
        this.changing.emit({ path: this.path, value })
      } else {
        clearTimeout(this.dirtyTimeout)
      }
      this.dirtyTimeout = setTimeout(() => {
        this.changed.emit({ path: this.path, value: this.control.value })
        this.dirtyTimeout = null
      }, 300)
    }
  }

  onChange(event: Event) {
    if (!this.fieldConfig.readonly) {
      const value = this.getInputValueFromEvent(event)
      window.requestAnimationFrame(() => {
        this.changed.emit({ path: this.path, value })
      })
    }
  }

  private getInputValueFromEvent(event: Event): string {
    const input = event.target as HTMLInputElement
    let value
    if (this.fieldConfig.fieldType === 'checkbox' && this.fieldConfig.multiple) {
      value = this.onMultiCheckboxChange(input)
    } else if (
      this.fieldConfig.fieldType === 'checkbox' ||
      this.fieldConfig.fieldType === 'radio'
    ) {
      value = this.onSimpleTickableChange(input)
    } else {
      value = input.value
    }
    return value
  }

  isTickableChecked(value: string): boolean {
    if (this.fieldConfig.multiple) {
      return !!(this.control as UntypedFormArray).controls.find(
        formControl => formControl.value === value
      )
    }
    return this.control.value === value
  }

  loadRecaptcha() {
    if (!this.fieldConfig.readonly && !this.isRecaptchaChecked()) {
      this.showRecaptcha = true
    }
  }

  isRecaptchaChecked(): boolean {
    return this.control.value && this.control.value.length > 1
  }

  recaptchaResolved(value: string) {
    this.control.setValue(value)
    this.showRecaptcha = false
    this.disabled = true
    this.control.valueChanges.pipe(take(1)).subscribe(val => {
      if (!val) {
        this.disabled = false
      }
    })
    window.requestAnimationFrame(() => {
      this.changed.emit({ path: this.path, value })
    })
  }

  private onSimpleTickableChange(target: HTMLInputElement) {
    const value = target?.checked ? target.value : ''
    this.control.setValue(value)
    return value
  }

  private onMultiCheckboxChange(target: HTMLInputElement) {
    const formArray = this.control as UntypedFormArray
    if (target.checked) {
      formArray.push(new UntypedFormControl(target.value))
    } else {
      let i = 0
      formArray.controls.forEach((control: UntypedFormControl) => {
        if (control.value === target.value) {
          formArray.removeAt(i)
          return
        }
        i++
      })
    }
    return formArray.value
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files[0]
    if (!this.fieldConfig.readonly) {
      if (file && this.formService.checkFile(this.fieldConfig, file)) {
        this.control.setValue(file)
      } else {
        this.fileInput.value = ''
      }
    }
    return file
  }

  onFileDropChange(droppedFiles: NgxFileDropEntry[]) {
    this.droppedFiles = droppedFiles.filter(droppedFile => droppedFile.fileEntry.isFile)
    if (!this.fieldConfig.readonly) {
      if (this.fieldConfig.multiple) {
        this.handleMultipleDroppedFiles()
      } else {
        this.handleSingleDroppedFile()
      }
    }
  }

  private handleMultipleDroppedFiles() {
    const formArray = this.control as UntypedFormArray
    formArray.clear()
    this.droppedFiles.forEach((droppedFile, index) => {
      const fileEntry = droppedFile.fileEntry as FileSystemFileEntry
      fileEntry.file((file: File) => {
        if (this.formService.checkFile(this.fieldConfig, file)) {
          formArray.push(new UntypedFormControl(File))
        } else {
          this.droppedFiles.splice(index, 1)
          this.cd.detectChanges()
        }
      })
    })
  }

  private handleSingleDroppedFile() {
    const fileEntry = this.droppedFiles[this.droppedFiles.length - 1]
      .fileEntry as FileSystemFileEntry
    fileEntry.file((file: File) => {
      if (this.formService.checkFile(this.fieldConfig, file)) {
        this.control.setValue(file)
      } else {
        this.droppedFiles = []
        this.cd.detectChanges()
      }
    })
  }

  createI18nToken(tokenName: string, prefix = ''): string {
    return this.i18nPrefix + prefix + this.toCamelCaseString(tokenName)
  }

  resolveLocalizedString(label: EbLocalizedString) {
    return label
      ? this.languageService.getActive().pipe(map(language => label?.[language] || label?.default))
      : of(null)
  }

  removeDroppedFile(event: Event, index) {
    event.preventDefault()
    if (this.fieldConfig.multiple) {
      this.formArray.removeAt(index)
      this.droppedFiles.splice(index, 1)
    } else {
      this.control.setValue(null)
      this.droppedFiles = []
      this.cd.detectChanges()
    }
  }

  toDottedString(str: string) {
    return this.toDashedString(str).replace(/-/g, '.')
  }
}
